/** Standalone web resource for initiating permanent account deletion. */

import { RADIUS, WithHalfdot } from "@elyxeer/ui";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DeleteAccountDrawer } from "~/components/account/DeleteAccountDrawer";

/**
 * A direct deletion URL for people using the browser rather than the installed app.
 *
 * Authentication is still required so one member cannot delete another member's
 * account. `LoginScreen` preserves this route through OTP verification.
 */
export function AccountDeletionRoute() {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(true);

  return (
    <Box
      sx={{
        minHeight: "100dvh",
        display: "grid",
        placeItems: "center",
        backgroundColor: "background.default",
        paddingInline: 3,
        paddingTop: "calc(24px + env(safe-area-inset-top))",
        paddingBottom: "calc(24px + env(safe-area-inset-bottom))",
      }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: "100%",
          maxWidth: 520,
          padding: { xs: 3, md: 4 },
          borderRadius: `${RADIUS.lg}px`,
        }}
      >
        <Typography variant="H1" sx={{ color: "text.primary" }}>
          <WithHalfdot size={11}>
            {t("profile.deleteAccount.resourceTitle")}
          </WithHalfdot>
        </Typography>
        <Typography
          variant="T2"
          sx={{ color: "text.secondary", marginTop: 1.5 }}
        >
          {t("profile.deleteAccount.resourceDescription")}
        </Typography>
        <Button
          variant="contained"
          color="error"
          size="large"
          fullWidth
          onClick={() => setConfirming(true)}
          data-testid="account.delete-open"
          sx={{ marginTop: 3 }}
        >
          {t("profile.deleteAccount.openConfirmation")}
        </Button>
      </Paper>

      <DeleteAccountDrawer
        open={confirming}
        onClose={() => setConfirming(false)}
      />
    </Box>
  );
}
